import { FoodFeedbacks } from '../FoodFeedbacks/FoodFeedbacks';
import { RatableItem } from '../RatableItem/RatableItem';
import { type useDetectChanges } from '../useDetectChanges';
import {
  RatableItemType,
  type RatablePairingItem,
  type RatableProductItem,
} from '../useRatableItems';
import { useThumbsBundleRating } from './useThumbsBundleRating';
import { useThumbsPairingRating } from './useThumbsPairingRating';
import { useThumbsProductRating } from './useThumbsProductRating';

type RatableListItemProps = {
  item: RatablePairingItem | RatableProductItem;
  onRating: (prevValue: boolean | null, newValue: boolean | null) => void;
  enableInitialFetch?: boolean;
  initialRating?: boolean | null;
  changeDetection: ReturnType<typeof useDetectChanges>;
};

export function RatableListItem(props: RatableListItemProps) {
  const { item, onRating, enableInitialFetch, initialRating, changeDetection } =
    props;

  const sharedProps: SharedRatableListItemProps = {
    enableInitialFetch,
    initialRating,
    changeDetection,
    renderChildren: ({
      rating,
      onRating: rendererOnRating,
    }: {
      rating: boolean | null;
      onRating: (newValue: boolean | null) => void;
      optimisticUpdateStatus: 'settled' | 'pending';
      productRatingId?: number | null;
    }) => (
      <RenderRatableItem
        item={item}
        rating={rating}
        onRating={(prevValue: boolean | null, newValue: boolean | null) => {
          rendererOnRating(newValue);
          onRating(prevValue, newValue);

          let ratableItemId;

          if ('bundleId' in item && item.bundleId) {
            ratableItemId = item.bundleId;
          } else if ('pairingId' in item && item.pairingId) {
            ratableItemId = item.pairingId;
          } else if ('productId' in item && item.productId) {
            ratableItemId = item.productId;
          }

          if (ratableItemId) {
            let ratableItemType: 'bundle' | 'pairing' | 'product';

            if (
              item.type === RatableItemType.Pairing &&
              Number.isInteger(item.bundleId)
            ) {
              ratableItemType = 'bundle';
            } else if (item.type === RatableItemType.Pairing) {
              ratableItemType = 'pairing';
            } else {
              ratableItemType = 'product';
            }

            changeDetection.updateRating({
              ratableItemId,
              ratableItemType,
              prevValue,
              newValue,
            });
          }
        }}
      />
    ),
  };

  if (
    item.type === RatableItemType.Pairing &&
    Number.isInteger(item.bundleId)
  ) {
    return (
      <RatableBundleListItem
        {...sharedProps}
        item={item as RatablePairingItem & { bundleId: number }}
      />
    );
  } else if (item.type === RatableItemType.Pairing) {
    return <RatablePairingListItem {...sharedProps} item={item} />;
  } else if (item.type === RatableItemType.Product) {
    return <RatableProductListItem {...sharedProps} item={item} />;
  }

  return null;
}

type RenderRatableItemProps = {
  item: RatablePairingItem | RatableProductItem;
  onRating: (prevValue: boolean | null, newValue: boolean | null) => void;
  rating: boolean | null;
};

function RenderRatableItem(props: RenderRatableItemProps) {
  const { item, onRating, rating } = props;

  return (
    <div>
      <RatableItem
        testid={`ratable-item-container-${
          item.type === RatableItemType.Pairing
            ? item.pairingId
            : item.productId
        }`}
        item={item}
        rating={rating}
        onRating={(prevValue, newValue) => {
          onRating(prevValue, newValue);
        }}
      />
      <FoodFeedbacks
        // The key here allows the component to unmount and remount,
        // resetting the state of the food feedbacks whenever the rating changes.
        key={String(rating)}
        item={item}
        rating={rating}
      />
    </div>
  );
}

type SharedRatableListItemProps = {
  renderChildren: (props: {
    rating: boolean | null;
    onRating: (newValue: boolean | null) => void;
    optimisticUpdateStatus: 'settled' | 'pending';
    productRatingId?: number | null;
  }) => React.ReactNode;
  enableInitialFetch?: boolean;
  initialRating?: boolean | null;
  changeDetection: ReturnType<typeof useDetectChanges>;
};

function RatablePairingListItem(
  props: SharedRatableListItemProps & { item: RatablePairingItem }
) {
  const { item, renderChildren, enableInitialFetch, initialRating } = props;

  const { rating, onRating, optimisticUpdateStatus } = useThumbsPairingRating({
    item,
    enableInitialFetch,
    initialRating,
  });

  return renderChildren({
    rating,
    onRating,
    optimisticUpdateStatus,
  });
}

function RatableBundleListItem(
  props: SharedRatableListItemProps & {
    item: RatablePairingItem & { bundleId: number };
  }
) {
  const { item, renderChildren, enableInitialFetch, initialRating } = props;

  const { rating, onRating, optimisticUpdateStatus } = useThumbsBundleRating({
    item,
    enableInitialFetch,
    initialRating,
  });

  return renderChildren({
    rating,
    onRating,
    optimisticUpdateStatus,
  });
}

function RatableProductListItem(
  props: SharedRatableListItemProps & { item: RatableProductItem }
) {
  const { item, renderChildren } = props;

  const { rating, onRating, optimisticUpdateStatus, productRatingId } =
    useThumbsProductRating({
      item,
    });

  return renderChildren({
    rating,
    onRating,
    optimisticUpdateStatus,
    productRatingId,
  });
}
